import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import NotificationBell from '../../components/admin/NotificationBell';
import MessageNotificationBell from '../../components/admin/MessageNotificationBell';

// Card component for dashboard metrics
interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  bgColor?: string;
  isLoading?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  bgColor = 'bg-white',
  isLoading = false
}) => (
  <div className={`${bgColor} rounded-lg shadow p-5 flex items-center`}>
    <div className="mr-4 bg-gray-100 p-3 rounded-full text-2xl">
      {icon}
    </div>
    <div>
      <div className="text-sm text-gray-500">{title}</div>
      <div className="text-2xl font-semibold mt-1">
        {isLoading ? (
          <div className="h-8 w-16 bg-gray-200 animate-pulse rounded"></div>
        ) : (
          value
        )}
      </div>
    </div>
  </div>
);

// Quick action button component
interface QuickActionProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  bgColor?: string;
}

const QuickAction: React.FC<QuickActionProps> = ({
  label,
  icon,
  onClick,
  bgColor = 'bg-[#3A5E63]'
}) => (
  <button
    onClick={onClick}
    className={`${bgColor} text-white p-3 rounded-lg flex items-center justify-center flex-col hover:opacity-90 transition-opacity`}
  >
    <span className="text-2xl mb-1">{icon}</span>
    <span className="text-xs">{label}</span>
  </button>
);

// Crew status component
interface CrewMember {
  id: string;
  name: string;
  clockedInAt: string;
}

// Appointment component
interface UpcomingAppointment {
  id: string;
  clientName: string;
  time: string;
  services: string[];
  staffName: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  // State for metrics
  const [isLoading, setIsLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeMembers, setActiveMembers] = useState(0);
  const [appointmentsToday, setAppointmentsToday] = useState(0);
  const [revenueMTD, setRevenueMTD] = useState(0);

  // State for crew on clock
  const [crewOnClock, setCrewOnClock] = useState<CrewMember[]>([]);

  // State for today's appointments
  const [upcomingAppointments, setUpcomingAppointments] = useState<UpcomingAppointment[]>([]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);

      try {
        // Get today's date range in UTC
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayEnd = new Date(todayStart);
        todayEnd.setDate(todayEnd.getDate() + 1);

        // Get month start for MTD revenue
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        // Fetch total users
        const { count: usersCount } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true });
        setTotalUsers(usersCount || 0);

        // Fetch active members
        const { count: membersCount } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .eq('membership_status', 'active');
        setActiveMembers(membersCount || 0);

        // Fetch appointments today
        const { count: appointmentsCount } = await supabase
          .from('appointments')
          .select('*', { count: 'exact', head: true })
          .gte('start_time', todayStart.toISOString())
          .lt('start_time', todayEnd.toISOString())
          .neq('status', 'cancelled');
        setAppointmentsToday(appointmentsCount || 0);

        // Fetch MTD revenue from payments
        const { data: paymentsData } = await supabase
          .from('payments')
          .select('amount')
          .gte('created_at', monthStart.toISOString())
          .eq('status', 'succeeded');

        const totalRevenue = paymentsData?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;
        setRevenueMTD(totalRevenue);

        // Fetch crew currently on clock (status = 'clocked_in')
        const { data: timeLogsData } = await supabase
          .from('crew_time_logs')
          .select(`
            id,
            clock_in_time,
            staff_id
          `)
          .eq('status', 'clocked_in')
          .order('clock_in_time', { ascending: false });

        // Get staff names for clocked-in crew
        if (timeLogsData && timeLogsData.length > 0) {
          const staffIds = timeLogsData.map(log => log.staff_id);
          const { data: staffData } = await supabase
            .from('team')
            .select('id, name')
            .in('id', staffIds);

          const staffMap = new Map(staffData?.map(s => [s.id, s.name]) || []);

          const crewList: CrewMember[] = timeLogsData.map((log: any) => ({
            id: log.id,
            name: staffMap.get(log.staff_id) || 'Unknown',
            clockedInAt: new Date(log.clock_in_time).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })
          }));
          setCrewOnClock(crewList);
        } else {
          setCrewOnClock([]);
        }

        // Fetch today's upcoming appointments
        const { data: appointmentsData } = await supabase
          .from('appointments')
          .select(`
            id,
            start_time,
            user_profiles:client_id (
              full_name
            ),
            team:staff_id (
              name
            ),
            appointment_services (
              services (
                name
              )
            )
          `)
          .gte('start_time', now.toISOString())
          .lt('start_time', todayEnd.toISOString())
          .neq('status', 'cancelled')
          .order('start_time', { ascending: true })
          .limit(5);

        const upcomingList: UpcomingAppointment[] = (appointmentsData || []).map((apt: any) => ({
          id: apt.id,
          clientName: apt.user_profiles?.full_name || 'Guest',
          time: new Date(apt.start_time + 'Z').toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/Chicago'
          }),
          services: apt.appointment_services?.map((as: any) => as.services?.name).filter(Boolean) || [],
          staffName: apt.team?.name || 'Any'
        }));
        setUpcomingAppointments(upcomingList);

      } catch (error) {
        // Silent fail - show zeros
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();

    // Refresh every 2 minutes
    const interval = setInterval(fetchDashboardData, 120000);
    return () => clearInterval(interval);
  }, []);

  // Format currency (amounts are stored in dollars)
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6 pt-2">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold text-[#344854]">Dashboard Overview</h1>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
          <div className="flex items-center gap-2">
            <MessageNotificationBell />
            <NotificationBell />
          </div>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Users"
          value={totalUsers.toLocaleString()}
          icon="👥"
          isLoading={isLoading}
        />
        <MetricCard
          title="Active Members"
          value={activeMembers.toLocaleString()}
          icon="💳"
          isLoading={isLoading}
        />
        <MetricCard
          title="Appointments Today"
          value={appointmentsToday}
          icon="📅"
          isLoading={isLoading}
        />
        <MetricCard
          title="Revenue (MTD)"
          value={formatCurrency(revenueMTD)}
          icon="💰"
          isLoading={isLoading}
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-5">
        <h2 className="text-lg font-medium mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickAction
            label="Users"
            icon="👤"
            onClick={() => navigate('/admin/users')}
          />
          <QuickAction
            label="Services"
            icon="📝"
            onClick={() => navigate('/admin/content')}
            bgColor="bg-[#5B8A72]"
          />
          <QuickAction
            label="Schedule"
            icon="🗓️"
            onClick={() => navigate('/admin/schedule')}
            bgColor="bg-[#6B7D8E]"
          />
          <QuickAction
            label="Time Reports"
            icon="⏱️"
            onClick={() => navigate('/admin/time-reports')}
            bgColor="bg-[#86A3A3]"
          />
        </div>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Crew On Clock */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">Crew On Clock</h2>
            <span className="text-sm text-gray-500">{crewOnClock.length} active</span>
          </div>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-12 bg-gray-100 animate-pulse rounded"></div>
              ))}
            </div>
          ) : crewOnClock.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-3xl mb-2">🏠</div>
              <p>No crew members clocked in</p>
            </div>
          ) : (
            <div className="space-y-3">
              {crewOnClock.map((crew) => (
                <div
                  key={crew.id}
                  className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                >
                  <div className="flex items-center">
                    <div className="h-3 w-3 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                    <span className="font-medium">{crew.name}</span>
                  </div>
                  <span className="text-sm text-gray-600">Since {crew.clockedInAt}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Today's Appointments */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">Upcoming Today</h2>
            <button
              onClick={() => navigate('/admin/schedule')}
              className="text-sm text-[#3A5E63] hover:underline"
            >
              View All
            </button>
          </div>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-gray-100 animate-pulse rounded"></div>
              ))}
            </div>
          ) : upcomingAppointments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-3xl mb-2">📭</div>
              <p>No more appointments today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingAppointments.map((apt) => (
                <div
                  key={apt.id}
                  className="p-3 border border-gray-200 rounded-lg hover:border-[#3A5E63] transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-[#344854]">{apt.clientName}</div>
                      <div className="text-sm text-gray-500">
                        {apt.services.join(', ') || 'Service'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-[#3A5E63]">{apt.time}</div>
                      <div className="text-xs text-gray-500">with {apt.staffName}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
